import BaseModel from './BaseModel';
import User from './User';
import Project from './Project';
import Change from './Change';
import moment from 'moment';
import type { Moment } from 'moment';

/** Class representing a routine action. */
export class RoutineAction {
    /**
     * Actions types constants
     */
    static ACTION_CREATE_CHANGE    = 'create_change';
    static ACTION_DUPLICATE_CHANGE = 'duplicate_change';
    static ACTION_NOTIFY           = 'notify';
    static ACTION_NOTIFY_UNCLAIMED_CHANGES = 'notify_unclaimed_changes';
    static ACTION_SET_STATUS       = 'set_status';
    static ACTION_ASSIGN           = 'assign';
    static ACTION_ASSIGN_CLIENT    = 'assign_client';
    static ACTION_MOVE_BOARD       = 'move_board';
    static ACTION_DISCARD          = 'discard';
    static ACTION_ADD_COMMENT      = 'add_comment';
    static ACTION_SEND_EMAIL       = 'send_email';
    static ACTION_SET_ESTIMATE     = 'set_estimate';

    type: string;
    options: AnyObj;

    constructor(properties: AnyObj) {
        this.type    = properties.type;
        this.options = properties.options;
    }

    static actionTypes() : string[] {
        return [
            RoutineAction.ACTION_CREATE_CHANGE,
            RoutineAction.ACTION_DUPLICATE_CHANGE,
            RoutineAction.ACTION_NOTIFY,
            RoutineAction.ACTION_NOTIFY_UNCLAIMED_CHANGES,
            RoutineAction.ACTION_SET_STATUS,
            RoutineAction.ACTION_ASSIGN,
            RoutineAction.ACTION_ASSIGN_CLIENT,
            RoutineAction.ACTION_MOVE_BOARD,
            RoutineAction.ACTION_DISCARD,
            RoutineAction.ACTION_ADD_COMMENT,
            RoutineAction.ACTION_SEND_EMAIL,
            RoutineAction.ACTION_SET_ESTIMATE,
        ];
    }
}

export type RoutineCondition = {
    path: string;
    operator: string;
    value: any;
};

/** OR group: AND of conditions inside the group. */
export type RoutineAnyOfGroup = {
    conditions: RoutineCondition[];
};

/** Class representing a routine. */
class Routine extends BaseModel {
    /**
     * Event groups for the UI selector
     */
    static EVENT_GROUPS: { key: string; events: string[] }[] = [
        {
            key: 'lifecycle',
            events: [
                'change.created',
                'change.claimed',
                'change.estimated',
                'change.unestimated',
                'change.planned',
                'change.unplanned',
                'change.sprint_added',
                'change.sprint_removed',
                'change.goal_added',
                'change.goal_removed',
                'change.assigned',
                'change.unassigned',
                'change.started',
                'change.team_review',
                'change.client_review',
                'change.pending_deployment',
                'change.deployed',
                'change.finished',
                'change.discarded',
                'change.restored',
                'change.spam_suspect',
                'change.spam_approved',
                'change.spam_rejected',
                'change.merged',
                'change.deleted',
                'change.moved',
                'change.updated',
            ]
        },
        {
            key: 'email',
            events: [
                'email.received',
                'email.reply_received',
                'email.acked',
                'attachment.created',
            ]
        },
        {
            key: 'mr',
            events: [
                'mr.open',
                'mr.reopen',
                'mr.approved',
                'mr.merge',
                'mr.comment',
                'issue.created',
            ]
        },
        {
            key: 'release',
            events: [
                'release.created',
                'release.updated',
            ]
        },
        {
            key: 'other',
            events: [
                'comment.created',
                'comment.mentioned',
                'reminder.reached',
                'track.created',
                'track.updated',
                'quote.created',
            ]
        }
    ];

    static CONDITION_OPERATORS = ['eq', 'neq', 'contains', 'regex', 'in'];

    _id: string;
    createdAt?: Moment;
    updatedAt?: Moment;
    name: string;
    enabled: boolean;
    priority: number;
    stopAfter: boolean;
    conditions: RoutineCondition[];
    /** OR of AND groups; combined with top-level `conditions` via AND. */
    anyOf: RoutineAnyOfGroup[];
    trigger: {
        frequency: string;
        events: string[];
    };
    actions: RoutineAction[];
    author?: User;
    project?: Project;
    change?: Change;

    constructor(properties: AnyObj) {
        super({});

        this._id       = properties._id;
        this.createdAt = properties.createdAt ? moment(properties.createdAt) : undefined;
        this.updatedAt = properties.updatedAt ? moment(properties.updatedAt) : undefined;
        this.name      = properties.name;
        this.enabled   = properties.enabled !== false;
        this.priority  = properties.priority ?? 100;
        this.stopAfter = !!properties.stopAfter;
        this.conditions = properties.conditions || [];
        this.anyOf = (properties.anyOf || []).map((group: AnyObj) => ({
            conditions: group?.conditions || []
        }));
        this.trigger   = properties.trigger || {
            frequency: '0 10 * * 1',
            events: []
        };
        this.actions   = (properties.actions || []).map((action: AnyObj) => new RoutineAction(action));
        this.author    = properties.author?._id ? new User(properties.author) : properties.author;
        this.project   = properties.project?._id ? new Project(properties.project) : properties.project;
        this.change  = properties.change?._id ? new Change(properties.change) : properties.change;
    }

    static eventTypes() : string[] {
        return Routine.EVENT_GROUPS.flatMap((g) => g.events);
    }
}

export default Routine;
